/**
 * First-impact prewarm — compile/link every shader variant and upload lazy
 * textures during the loading overlay so the first cannonball does not hitch.
 *
 * IntactWall only draws the exterior Mesh. The first topology-changing hit
 * mounts BatchedMesh (USE_BATCHING) + interior face programs, transparent fade
 * clones, chips, and ImpactFx rings. Each new program is a sync compile+link
 * (often 100ms+, several at once = the freeze). drei <Preload all /> cannot
 * see materials that are not yet in the graph.
 */
import * as THREE from 'three';
import {
  DESTRUCTION_PROFILES,
  PROFILE_IDS,
  type ProfileId,
} from '../../services/destructionProfiles';
import {
  cloneProfileMaterial,
  createProfileMaterial,
  type ProfileMaterialSettings,
} from '../../materials/MaterialRegistry';
import { DEFAULT_CONCRETE_SETTINGS } from '../../materials/authoring/ConcreteMaterial';
import {
  ensureDecalAtlasLoaded,
  getDecalAtlasTextures,
} from '../../materials/core/DamageDecalAtlas';
import { subdivideCell } from '../../services/fracture/voronoiFracture';
import type { FractureCell } from '../../services/fracture/voronoiFracture';
import { createDamageField } from '../../structures/demolition/demolitionDamageField';
import { stampFactDamage } from './Phase1DamageLayer';
import type { DamageFact } from '../../structures/demolition/demolitionDamageLedger';
import type { PowderResidueController } from './PowderResidueLayer';

export const FIRST_HIT_PREWARM_NAME = '__firstHitPrewarm';

export interface FirstHitPrewarmStats {
  compileMs: number;
  textureMs: number;
  cpuMs: number;
  totalMs: number;
  meshCount: number;
  materialCount: number;
}

export interface FirstHitFxWarm {
  warm?: () => void;
}

const FACES = ['exterior', 'interior'] as const;
const PROFILE_SETTINGS: ProfileMaterialSettings = {
  concrete: DEFAULT_CONCRETE_SETTINGS,
};

function addShadowed(
  group: THREE.Group,
  object: THREE.Object3D,
  name: string,
): void {
  object.name = name;
  object.castShadow = true;
  object.receiveShadow = true;
  // Sit inside the demo key-light shadow camera (and the perspective
  // frustum) so compile() emits the BatchedMesh *shadow-depth* programs.
  // The caller renders to an offscreen target, then removes the group.
  object.position.set(0, 2.1, 0);
  object.scale.setScalar(0.04);
  object.frustumCulled = false;
  group.add(object);
}

function addBatched(
  group: THREE.Group,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  name: string,
): THREE.BatchedMesh {
  const pos = geometry.getAttribute('position');
  const idx = geometry.getIndex();
  const vertexCount = Math.max(1, pos?.count ?? 8);
  const indexCount = Math.max(1, idx?.count ?? vertexCount);
  const batch = new THREE.BatchedMesh(1, vertexCount, indexCount, material);
  const geometryId = batch.addGeometry(geometry);
  batch.addInstance(geometryId);
  addShadowed(group, batch, name);
  return batch;
}

/**
 * Hidden warmup graph: Mesh + BatchedMesh + transparent fade for every
 * profile/face, plus chip / shock / burst stand-ins.
 */
export function buildFirstHitWarmupGroup(
  liveMaterials: THREE.Material[],
  profileIds: readonly ProfileId[] = PROFILE_IDS,
): {
  group: THREE.Group;
  ownedMaterials: THREE.Material[];
  ownedGeometries: THREE.BufferGeometry[];
} {
  const group = new THREE.Group();
  group.name = FIRST_HIT_PREWARM_NAME;
  group.matrixAutoUpdate = false;

  const box = new THREE.BoxGeometry(0.02, 0.02, 0.02);
  const torus = new THREE.TorusGeometry(0.2, 0.03, 6, 16);
  const disc = new THREE.CircleGeometry(0.16, 12);
  const ownedGeometries: THREE.BufferGeometry[] = [box, torus, disc];
  const ownedMaterials: THREE.Material[] = [];

  const addPair = (material: THREE.Material, label: string, own: boolean) => {
    if (own) ownedMaterials.push(material);
    addShadowed(group, new THREE.Mesh(box, material), `${label}-mesh`);
    addBatched(group, box, material, `${label}-batch`);
  };

  liveMaterials.forEach((material, index) => {
    addPair(material, `live-${index}`, false);
    const fade = cloneProfileMaterial(material);
    fade.transparent = true;
    fade.depthWrite = false;
    fade.opacity = 0.45;
    addPair(fade, `live-${index}-fade`, true);
  });

  for (const id of profileIds) {
    const skin = DESTRUCTION_PROFILES[id].skin;
    for (const face of FACES) {
      const params = face === 'exterior' ? skin.outer : skin.interior;
      const material = createProfileMaterial(id, params, face, PROFILE_SETTINGS);
      addPair(material, `${id}-${face}`, true);
      const fade = cloneProfileMaterial(material);
      fade.transparent = true;
      fade.depthWrite = false;
      fade.opacity = 0.45;
      addPair(fade, `${id}-${face}-fade`, true);
    }
  }

  const chip = new THREE.MeshBasicMaterial({
    color: '#6a6560',
    transparent: true,
    opacity: 0.42,
    depthWrite: false,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  ownedMaterials.push(chip);
  addShadowed(group, new THREE.Mesh(box, chip), 'chip');

  const shock = new THREE.MeshBasicMaterial({
    color: '#ff6a3d',
    transparent: true,
    opacity: 0.7,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  ownedMaterials.push(shock);
  const shockMesh = new THREE.Mesh(torus, shock);
  shockMesh.name = 'shock';
  shockMesh.position.set(0, 2.1, 0);
  shockMesh.frustumCulled = false;
  group.add(shockMesh);

  const burstLine = new THREE.LineBasicMaterial({
    color: '#bd916d',
    transparent: true,
    opacity: 0.6,
    depthWrite: false,
  });
  const burstCore = new THREE.MeshBasicMaterial({
    color: '#b78a63',
    transparent: true,
    opacity: 0.4,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  ownedMaterials.push(burstLine, burstCore);
  const lineGeom = new THREE.BufferGeometry();
  lineGeom.setAttribute(
    'position',
    new THREE.Float32BufferAttribute([0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1, 0], 3),
  );
  ownedGeometries.push(lineGeom);
  const lines = new THREE.LineSegments(lineGeom, burstLine);
  lines.name = 'burst-line';
  lines.position.set(0, 2.1, 0);
  lines.frustumCulled = false;
  group.add(lines);
  const core = new THREE.Mesh(disc, burstCore);
  core.name = 'burst-core';
  core.position.set(0, 2.1, 0);
  core.frustumCulled = false;
  group.add(core);

  const residue = new THREE.MeshStandardMaterial({
    color: '#8a5a3c',
    transparent: true,
    depthWrite: false,
    roughness: 0.96,
    metalness: 0,
    envMapIntensity: 0.35,
  });
  ownedMaterials.push(residue);
  addShadowed(group, new THREE.Mesh(box, residue), 'residue');

  // Cannon + trail: first *drawn* instance is what emits USE_INSTANCING.
  const sphere = new THREE.SphereGeometry(0.08, 16, 12);
  ownedGeometries.push(sphere);
  const cannonMat = new THREE.MeshStandardMaterial({ color: '#8a9098', roughness: 0.32, metalness: 0.72 });
  const trailMat = new THREE.MeshBasicMaterial({
    color: '#ffffff',
    transparent: true,
    opacity: 0.54,
    depthWrite: false,
    vertexColors: true,
  });
  ownedMaterials.push(cannonMat, trailMat);
  const placeInstanced = (geometry: THREE.BufferGeometry, material: THREE.Material, name: string) => {
    const instanced = new THREE.InstancedMesh(geometry, material, 2);
    instanced.castShadow = true;
    instanced.receiveShadow = true;
    instanced.frustumCulled = false;
    instanced.name = name;
    const dummy = new THREE.Object3D();
    dummy.position.set(0, 2.1, 1.2);
    dummy.scale.setScalar(1);
    dummy.updateMatrix();
    instanced.setMatrixAt(0, dummy.matrix);
    instanced.setMatrixAt(1, dummy.matrix);
    instanced.instanceMatrix.needsUpdate = true;
    group.add(instanced);
  };
  placeInstanced(sphere, cannonMat, 'cannon-sphere-inst');
  placeInstanced(box, trailMat, 'projectile-inst-0');
  addBatched(group, box, cannonMat, 'projectile-batch-0');

  return { group, ownedMaterials, ownedGeometries };
}

export function disposeFirstHitWarmupGroup(built: {
  group: THREE.Group;
  ownedMaterials: THREE.Material[];
  ownedGeometries: THREE.BufferGeometry[];
}): void {
  built.group.removeFromParent();
  built.group.clear();
  for (const material of built.ownedMaterials) material.dispose();
  for (const geometry of built.ownedGeometries) geometry.dispose();
}

function dummyDamageFact(): DamageFact {
  return {
    sequence: 0,
    simTime: 0,
    kind: 'kinetic',
    position: { x: 0, y: 0, z: 0.3 },
    outwardNormal: { x: 0, y: 0, z: 1 },
    radius: 0.2,
    appliedEnergy: 8,
    normalizedSeverity: 0.2,
    damageBefore: 0,
    damageAfter: 0.2,
    topologyChanged: false,
  };
}

export function warmFirstHitCpuPaths(opts: {
  cell?: FractureCell | null;
  residue?: PowderResidueController | null;
  fx?: FirstHitFxWarm | null;
  wallHalf?: { x: number; y: number; z: number };
}): { subdivideMs: number; stampMs: number; residueMs: number } {
  const out = { subdivideMs: 0, stampMs: 0, residueMs: 0 };

  if (opts.cell) {
    const t0 = performance.now();
    subdivideCell(opts.cell, 3, () => 0.37);
    out.subdivideMs = performance.now() - t0;
  }

  const half = opts.wallHalf ?? { x: 3, y: 2.1, z: 0.3 };
  const tStamp = performance.now();
  const field = createDamageField({ x: half.x, y: half.y, z: half.z });
  stampFactDamage(
    field,
    dummyDamageFact(),
    new THREE.Vector3(0, 0, half.z),
    new THREE.Vector3(0, 0, 1),
  );
  out.stampMs = performance.now() - tStamp;

  if (opts.residue) {
    const t0 = performance.now();
    opts.residue.stamp({
      x: 0,
      z: 0,
      volume: 1e-4,
      material: 'concrete' as ProfileId,
    });
    opts.residue.clear();
    out.residueMs = performance.now() - t0;
  }

  opts.fx?.warm?.();
  return out;
}

export interface RunFirstHitPrewarmOpts {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.Camera;
  liveMaterials: THREE.Material[];
  profileIds?: readonly ProfileId[];
  cell?: FractureCell | null;
  residue?: PowderResidueController | null;
  fx?: FirstHitFxWarm | null;
  wallHalf?: { x: number; y: number; z: number };
}

/**
 * Compile every first-hit program, upload decal atlases, dry-run CPU paths,
 * then strip the warmup group so no stray mesh can appear in captures.
 */
export async function runFirstHitPrewarm(
  opts: RunFirstHitPrewarmOpts,
): Promise<FirstHitPrewarmStats> {
  const totalT0 = performance.now();
  const built = buildFirstHitWarmupGroup(opts.liveMaterials, opts.profileIds ?? PROFILE_IDS);
  opts.scene.add(built.group);

  const textureT0 = performance.now();
  try {
    await ensureDecalAtlasLoaded();
  } catch {
    // Atlas is best-effort; compile still proceeds.
  }
  const atlases = getDecalAtlasTextures();
  if (atlases.concrete) opts.renderer.initTexture(atlases.concrete);
  if (atlases.brick) opts.renderer.initTexture(atlases.brick);
  if (atlases.stone) opts.renderer.initTexture(atlases.stone);
  if (atlases.wood) opts.renderer.initTexture(atlases.wood);
  const textureMs = performance.now() - textureT0;

  const meshCount = countWarmupMeshes(built.group);
  const materialCount = built.ownedMaterials.length + opts.liveMaterials.length;
  const compileT0 = performance.now();
  const prevTarget = opts.renderer.getRenderTarget();
  try {
    // Compile + draw on the live target so shadow-map and instancing programs
    // match production. The page overlay is fully opaque during this window.
    if (opts.renderer.shadowMap && !opts.renderer.shadowMap.enabled) {
      opts.renderer.shadowMap.enabled = true;
    }
    opts.renderer.compile(opts.scene, opts.camera);
  } finally {
    opts.renderer.setRenderTarget(prevTarget);
    disposeFirstHitWarmupGroup(built);
  }
  const compileMs = performance.now() - compileT0;

  const cpuT0 = performance.now();
  warmFirstHitCpuPaths({
    cell: opts.cell,
    residue: opts.residue,
    fx: opts.fx,
    wallHalf: opts.wallHalf,
  });
  const cpuMs = performance.now() - cpuT0;

  return {
    compileMs,
    textureMs,
    cpuMs,
    totalMs: performance.now() - totalT0,
    meshCount,
    materialCount,
  };
}

function countWarmupMeshes(group: THREE.Group): number {
  let n = 0;
  group.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (mesh.isMesh || (object as THREE.BatchedMesh).isBatchedMesh || (object as THREE.Line).isLine) {
      n += 1;
    }
  });
  return n;
}
